<template>
  <div
    class="gmb-labels-mass-edit"
    :class="{ disabled: !canEditFacilityLabels }"
  >
    <v-row class="max-w-560">
      <v-col class="d-flex align-center pb-0 max-w-50px">
        <v-checkbox
          class="mt-0"
          v-model="checkbox"
          @change="onCheckboxClicked"
          :disabled="!canEditFacilityLabels"
        ></v-checkbox>
      </v-col>
      <v-col class="d-flex align-center pb-0">
        <paragraph class="border-accent">
          {{ $t("message.facilityDetails.labels.facilitiesLabels") }}
        </paragraph>
      </v-col>
    </v-row>
    <v-row v-if="!labels.length">
      <v-col class="max-w-50px" />
      <v-col>
        <div class="description">
          {{ $t("message.facilityDetails.labels.noLabelsSelected") }}
        </div>
        <InfoBox
          class="mt-2"
          :text="$t('message.facilityDetails.labels.editMessage')"
        />
        <div class="actions" v-if="checkbox">
          <div class="action" v-for="action in actions" :key="action.type">
            <div class="description muted">
              {{ $t(action.msg) }}
            </div>
            <v-btn v-bind="action.btnProps" @click="handleAction(action.type)">
              {{ $t(action.btnLabel) }}
            </v-btn>
            <v-tooltip right :max-width="tooltipMaxWidth">
              <template v-slot:activator="{ on }">
                <div class="info-icon" v-on="on">
                  <v-icon color="lime">mdi-information</v-icon>
                </div>
              </template>
              {{ $t(action.desc) }}
            </v-tooltip>
          </div>
        </div>
      </v-col>
    </v-row>
    <template v-if="labels.length">
      <v-row>
        <v-col class="max-w-50px" />
        <v-col>
          <div class="active-action">
            <v-row>
              <v-col cols="11" class="d-flex align-center">
                <span class="section-title">{{ activeActionLabel }}:</span>
              </v-col>
              <v-col cols="1">
                <v-btn
                  icon
                  color="primary"
                  @click="handleAction(selectedActionType)"
                >
                  <v-icon>
                    icon-edit
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="max-w-50px" />
        <v-col>
          <div class="chosen-labels">
            <v-chip v-for="(label, index) in labels" :key="`${label}_${index}`">
              {{ label }}
            </v-chip>
          </div>

          <div class="change-action" v-if="selectedActionType">
            <div class="description muted mb-0">
              {{ $t("message.facilityDetails.labels.changeType") }}
            </div>
            <v-btn
              v-for="action in notActiveActions"
              :key="action.type"
              text
              color="primary"
              @click="changeAction(action.type)"
              class="ml-4"
            >
              {{ $t(action.btnLabel) }}
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </template>

    <EditLabelsDialog
      v-if="selectedAction"
      :is-open="dialog.addOrSet"
      :labels="labels"
      :title="selectedAction.btnLabel"
      :description="selectedAction.desc"
      @close="dialog.addOrSet = null"
      @save="handleSave"
    />

    <GmbLabelsDeleteDialog
      :is-open="dialog.delete"
      :facilities-ids="facilitiesIds"
      :initially-selected-labels="labels"
      @delete="handleSave"
      @close="dialog.delete = false"
    />
  </div>
</template>

<script>
import Paragraph from "@/components/ui/Paragraph";
import EditLabelsDialog from "@/components/FacilityDetails/Dialog/EditLabelsDialog.vue";
import GmbLabelsDeleteDialog from "@/components/FacilityDetails/GmbLabels/GmbLabelsDeleteDialog";
import InfoBox from "@/components/ui/InfoBox.vue";

import { DialogHelper } from "@/helpers/dialog-helper";
import { GmbActionType } from "@/enums/gmb-attribute-action-type";
import { permissionsInfo } from "@/helpers/mixins/permissions-info";

export default {
  name: "GmbLabelsMassEdit",
  components: {
    Paragraph,
    EditLabelsDialog,
    GmbLabelsDeleteDialog,
    InfoBox
  },
  mixins: [permissionsInfo],
  data() {
    return {
      dialog: {
        addOrSet: false,
        delete: false
      },
      checkbox: false,
      labels: [],
      tooltipMaxWidth: 436,
      actions: [],
      facilitiesIds: [],
      actionTypes: GmbActionType,
      selectedAction: null,
      selectedActionType: null
    };
  },
  computed: {
    activeActionLabel() {
      const labelKey = "message.facilityDetails.labels.";
      if (this.selectedActionType === this.actionTypes.ADD) {
        return this.$t(labelKey + "addingLabels");
      } else if (this.selectedActionType === this.actionTypes.DELETE) {
        return this.$t(labelKey + "deletingLabels");
      } else if (this.selectedActionType === this.actionTypes.SET) {
        return this.$t(labelKey + "overwritingLabels");
      } else {
        return "";
      }
    },
    notActiveActions() {
      return this.actions.filter(
        ({ type }) => type !== this.selectedActionType
      );
    }
  },
  mounted() {
    this.facilitiesIds = this.$route.params.ids.split(",");
  },
  created() {
    this.setActions();
  },
  methods: {
    setActions() {
      this.actions = [
        {
          type: this.actionTypes.ADD,
          msg: "message.facilityDetails.labels.addLabelsQuestion",
          desc: "message.facilityDetails.labels.addLabelsDescription",
          btnLabel: "message.facilityDetails.labels.addLabels",
          btnProps: {
            color: "primary"
          }
        },
        {
          type: this.actionTypes.DELETE,
          msg: "message.facilityDetails.labels.deleteLabelsQuestion",
          desc: "message.facilityDetails.labels.deleteLabelsDescription",
          btnLabel: "message.facilityDetails.labels.deleteLabels",
          btnProps: {
            color: "primary"
          }
        },
        {
          type: this.actionTypes.SET,
          msg: "message.facilityDetails.labels.overwriteLabelsQuestion",
          desc: "message.facilityDetails.labels.overwriteLabelsDescription",
          btnLabel: "message.facilityDetails.labels.overwriteLabels",
          btnProps: {
            color: "primary",
            outlined: true
          }
        }
      ];
    },
    onCheckboxClicked(val) {
      this.$emit("update:checked", val);
      if (!val) {
        this.labels = [];
        this.selectedAction = null;
        this.selectedActionType = null;
        this.$emit("update:labels", this.labels);
      }
    },
    changeAction(actionType) {
      DialogHelper.showConfirmDialog(
        {
          text: this.$t("message.facilityDetails.labels.changeTypeConfirm"),
          okLabelKey: "message.common.continue"
        },
        () => {
          this.handleAction(actionType);
        }
      );
    },
    handleAction(actionType) {
      if (this.selectedAction && this.selectedAction.type !== actionType) {
        this.labels = [];
      }

      this.selectedAction = this.actions.find(
        action => action.type === actionType
      );

      if (this.selectedAction.type === GmbActionType.DELETE) {
        this.dialog.delete = true;
      } else {
        this.dialog.addOrSet = true;
      }
    },
    handleSave(labels) {
      this.labels = labels;
      this.selectedActionType = this.selectedAction.type;
      this.$emit("update:type", this.selectedActionType);
      this.$emit("update:labels", this.labels);
    }
  }
};
</script>

<style lang="scss" scoped>
.gmb-labels-mass-edit {
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  padding: $spacing * 1.5 0;
}

.section-title {
  @include font-size(16px);
  line-height: 24px;
  font-weight: 600;
  color: $grey-darker;
}

.description {
  @include font-size(14px);
  line-height: 17px;
  color: $grey-darker;
  margin-bottom: $spacing;

  &.muted {
    line-height: 19px;
    opacity: 0.7;
  }
}

.actions {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-right: -$spacing * 5.75;

  .action {
    flex-basis: 170px;
    position: relative;
    margin-top: $spacing * 5;
    margin-right: $spacing * 5.75;

    .description {
      text-align: center;
      margin-bottom: $spacing * 2.5;
    }

    .info-icon {
      display: inline;
      position: absolute;
      top: 6px;
      right: -30px;
    }
  }

  .v-btn {
    width: 100%;
  }
}

.active-action {
  border-bottom: 1px solid $light-3;
}

.chosen-labels {
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  .v-chip {
    height: unset;
    max-height: 46px;
    border-radius: 30px;
    background-color: $light-4 !important;
    @include font-size(14px);
    line-height: 17px;
    padding: $spacing * 1.8125 $spacing * 2.5;
    margin-bottom: $spacing * 0.5;

    &:not(:last-child) {
      margin-right: $spacing;
    }
  }
}

.change-action {
  display: flex;
  align-items: center;
  margin-top: $spacing * 2.5;
  padding-top: $spacing;
  border-top: 1px solid $light-3;
}

.max-w-560 {
  max-width: 560px;
}

.border-accent {
  @include borderAccent($lime);
}

.v-btn {
  text-transform: none;
}
</style>
